/* ============================================
   EFP CONFIGURATOR — PRICING ENGINE
   Enviro Flex Pack © 2026
   ============================================ */

(function () {
  'use strict';

  /* ------------------------------------------
     STATE
  ------------------------------------------ */
  const state = {
    pouch_type: null,
    material: null,
    size: null,
    addons: [],
    quantity: null,
    logistics: 'standard',
    artwork_file: null
  };

  /* ------------------------------------------
     DATA FROM METAFIELDS
  ------------------------------------------ */
  const DATA = window.EFP_DATA;
  const pricingMatrix = DATA.pricingMatrix.pricing_matrix;
  const materials = DATA.materials.materials;
  const ecoRules = DATA.ecoRules.eco_incentive_rules;
  const logisticsOptions = DATA.logisticsOptions.logistics_options;
  const addonCosts = DATA.addonCosts.addon_costs;
  const quantityTiers = DATA.quantityTiers.quantity_tiers;

  /* ------------------------------------------
     SIZE OPTIONS PER POUCH TYPE
  ------------------------------------------ */
  const SIZE_MAP = {
    stand_up_pouch: [
      { value: '4oz',  label: '4',  unit: 'oz' },
      { value: '8oz',  label: '8',  unit: 'oz' },
      { value: '12oz', label: '12', unit: 'oz' },
      { value: '5lb',  label: '5',  unit: 'lbs' }
    ],
    block_bottom_pouch: [
      { value: '12oz', label: '12', unit: 'oz' },
      { value: '16oz', label: '16', unit: 'oz' }
    ],
    side_gusset_pouch: [
      { value: '16oz', label: '16', unit: 'oz' },
      { value: '5lb',  label: '5',  unit: 'lbs' }
    ],
    flat_pouch: []
  };

  /* ------------------------------------------
     MATERIAL SPEC MAP
  ------------------------------------------ */
  const MATERIAL_SPECS = {
    plastic_foil: {
      thickness: '5.4 mil',
      barrier: 'OTR < 1.0',
      finish: 'Matte'
    },
    kraft_foil: {
      thickness: '5.4 mil',
      barrier: 'OTR < 1.0',
      finish: 'Natural Kraft / White Kraft'
    },
    pe_duplex: {
      thickness: '4.8 mil',
      barrier: 'OTR < 1.5',
      finish: 'Matte Soft Touch'
    },
    kraft_pla: {
      thickness: '5.2 mil',
      barrier: 'OTR < 2.0',
      finish: 'Natural Kraft / White Kraft'
    }
  };

  /* ------------------------------------------
     DOM HELPERS
  ------------------------------------------ */
  const $ = (id) => document.getElementById(id);
  const $$ = (sel) => document.querySelectorAll(sel);

  /* ------------------------------------------
     INIT
  ------------------------------------------ */
  function init() {
    bindCardSelectors();
    bindAddons();
    bindLogistics();
    bindArtworkUpload();
    bindAddToCart();
    highlightNavOnScroll();
    watchCartForEFPRemoval();

    // Set default logistics
    const defaultLogistics = $$('.efp-card--logistics');
    if (defaultLogistics.length) {
      defaultLogistics[0].classList.add('active');
    }
  }

  /* ------------------------------------------
     CARD SELECTORS — Pouch Type & Material
  ------------------------------------------ */
  function bindCardSelectors() {
    $$('[data-type="pouch_type"]').forEach((btn) => {
      btn.addEventListener('click', () => {
        selectCard('pouch_type', btn);
        state.pouch_type = btn.dataset.value;
        state.size = null;
        state.material = null;

        // Reset material availability
        updateMaterialAvailability(state.pouch_type);

        // Rebuild size grid
        buildSizeGrid(state.pouch_type);

        // Clear material selection
        $$('[data-type="material"]').forEach(m => m.classList.remove('active'));

        recalculate();
      });
    });

    $$('[data-type="material"]').forEach((btn) => {
      btn.addEventListener('click', () => {
        if (btn.classList.contains('disabled')) return;
        selectCard('material', btn);
        state.material = btn.dataset.value;
        updateTechSpecs(state.material);
        recalculate();
      });
    });
  }

  /* ------------------------------------------
     SELECT CARD HELPER (single select per type)
  ------------------------------------------ */
  function selectCard(type, activeBtn) {
    $$(`[data-type="${type}"]`).forEach((btn) => {
      btn.classList.remove('active');
      btn.setAttribute('aria-pressed', 'false');
    });
    activeBtn.classList.add('active');
    activeBtn.setAttribute('aria-pressed', 'true');
  }

  /* ------------------------------------------
     MATERIAL AVAILABILITY
  ------------------------------------------ */
  function updateMaterialAvailability(pouchTypeId) {
    $$('[data-type="material"]').forEach((btn) => {
      const availableFor = btn.dataset.availableFor
        ? btn.dataset.availableFor.split(',')
        : [];
      if (availableFor.includes(pouchTypeId)) {
        btn.classList.remove('disabled');
        btn.removeAttribute('disabled');
      } else {
        btn.classList.add('disabled');
        btn.setAttribute('disabled', 'true');
        btn.classList.remove('active');
      }
    });
  }

  /* ------------------------------------------
     SIZE GRID (STEP 3)
  ------------------------------------------ */
  function buildSizeGrid(pouchTypeId) {
    const grid = $('efp-size-grid');
    if (!grid) return;

    const sizes = SIZE_MAP[pouchTypeId] || [];

    if (!sizes.length) {
      grid.innerHTML = '<p class="efp-placeholder">No size options available for this pouch type.</p>';
      return;
    }

    grid.innerHTML = sizes.map((size) => {
      // Check if this size has any pricing data
      const pouchData = pricingMatrix[pouchTypeId];
      const hasData = pouchData && pouchData[size.value];
      const disabledClass = hasData ? '' : 'disabled';

      return `
        <button
          class="efp-size-btn ${disabledClass}"
          data-size="${size.value}"
          aria-label="${size.label} ${size.unit}"
        >
          <span class="efp-size-btn__value">${size.label}</span>
          <span class="efp-size-btn__unit">${size.unit}</span>
        </button>
      `;
    }).join('');

    // Bind size button clicks
    grid.querySelectorAll('.efp-size-btn').forEach((btn) => {
      btn.addEventListener('click', () => {
        grid.querySelectorAll('.efp-size-btn').forEach(b => b.classList.remove('active'));
        btn.classList.add('active');
        state.size = btn.dataset.size;
        recalculate();
      });
    });
  }

  /* ------------------------------------------
     ADD-ONS (STEP 4) — multi select
  ------------------------------------------ */
  function bindAddons() {
    $$('[data-type="addon"]').forEach((btn) => {
      btn.addEventListener('click', () => {
        const id = btn.dataset.value;
        btn.classList.toggle('active');

        if (btn.classList.contains('active')) {
          if (!state.addons.includes(id)) state.addons.push(id);
        } else {
          state.addons = state.addons.filter(a => a !== id);
        }
        recalculate();
      });
    });
  }

  /* ------------------------------------------
     LOGISTICS (STEP 7)
  ------------------------------------------ */
  function bindLogistics() {
    $$('[data-type="logistics"]').forEach((btn) => {
      btn.addEventListener('click', () => {
        $$('[data-type="logistics"]').forEach(b => b.classList.remove('active'));
        btn.classList.add('active');
        state.logistics = btn.dataset.value;
        recalculate();
      });
    });
  }

  /* ------------------------------------------
     ARTWORK UPLOAD (STEP 5)
  ------------------------------------------ */
  function bindArtworkUpload() {
    const input = $('efp-artwork-input');
    const zone = $('efp-upload-zone');
    const filename = $('efp-upload-filename');

    if (!input || !zone) return;

    input.addEventListener('change', () => {
      if (input.files && input.files[0]) {
        state.artwork_file = input.files[0];
        filename.textContent = `✓ ${input.files[0].name}`;
      }
    });

    // Drag and drop
    zone.addEventListener('dragover', (e) => {
      e.preventDefault();
      zone.classList.add('dragover');
    });

    zone.addEventListener('dragleave', () => {
      zone.classList.remove('dragover');
    });

    zone.addEventListener('drop', (e) => {
      e.preventDefault();
      zone.classList.remove('dragover');
      const file = e.dataTransfer.files[0];
      if (file) {
        state.artwork_file = file;
        filename.textContent = `✓ ${file.name}`;
      }
    });
  }

  /* ------------------------------------------
     TECH SPECS UPDATE (SIDEBAR)
  ------------------------------------------ */
  function updateTechSpecs(materialId) {
    const specs = MATERIAL_SPECS[materialId];
    if (!specs) return;

    const thickness = $('efp-spec-thickness');
    const barrier = $('efp-spec-barrier');
    const finish = $('efp-spec-finish');

    if (thickness) thickness.textContent = specs.thickness;
    if (barrier) barrier.textContent = specs.barrier;
    if (finish) finish.textContent = specs.finish;
  }

  /* ------------------------------------------
     PRICE CALCULATION ENGINE
  ------------------------------------------ */
  function recalculate() {
    const { pouch_type, material, size, addons, quantity, logistics } = state;

    // Update quantity tier cards with per-unit prices
    updateTierPrices(pouch_type, material, size);

    // Guard: need all core selections
    if (!pouch_type || !material || !size || !quantity) {
      updateSidebar(null, null, null, null);
      toggleAddToCart(false);
      return;
    }

    // 1. Base unit price from matrix
    const unitPrice = getUnitPrice(pouch_type, material, size, quantity);
    if (unitPrice === null) {
      updateSidebar(null, null, null, null);
      toggleAddToCart(false);
      return;
    }

    // 2. Add-on costs per unit
    const addonCostPerUnit = calcAddonCosts(addons);

    // 3. Logistics multiplier
    const logisticsMultiplier = getLogisticsMultiplier(logistics);

    // 4. Subtotal before eco incentive
    const pricePerUnit = (unitPrice + addonCostPerUnit) * logisticsMultiplier;
    const subtotal = pricePerUnit * quantity;

    // 5. Eco incentive
    const ecoDiscount = calcEcoIncentive(material, quantity);

    // 6. Final total
    const total = subtotal - ecoDiscount;

    // 7. Update sidebar
    updateSidebar(pricePerUnit, quantity, ecoDiscount, total);
    updatePreviewLabel();
    toggleAddToCart(true);
  }

  /* ------------------------------------------
     GET UNIT PRICE FROM MATRIX
  ------------------------------------------ */
  function getUnitPrice(pouchType, material, size, quantity) {
    try {
      const price = pricingMatrix[pouchType][size][material][String(quantity)];
      return (price !== null && price !== undefined) ? price : null;
    } catch (e) {
      return null;
    }
  }

  /* ------------------------------------------
     UPDATE TIER CARDS WITH PRICES
  ------------------------------------------ */
  function updateTierPrices(pouchType, material, size) {
    const tiers = quantityTiers.quantity_tiers || quantityTiers;
    if (!tiers || !Array.isArray(tiers)) return;
    tiers.forEach((tier) => {
      const priceEl = document.querySelector(
        `[data-tier-units="${tier.units}"]`
      );
      if (!priceEl) return;

      if (!pouchType || !material || !size) {
        priceEl.textContent = '—';
        return;
      }

      const price = getUnitPrice(pouchType, material, size, String(tier.units));
      priceEl.textContent = price !== null
        ? `$${price.toFixed(2)} / ea`
        : 'N/A';
    });
  }

  /* ------------------------------------------
     ADD-ON COST CALCULATOR
  ------------------------------------------ */
  function calcAddonCosts(selectedAddons) {
    const costs = addonCosts.addon_costs || addonCosts;
    return costs.reduce((total, addon) => {
      if (selectedAddons.includes(addon.id)) {
        total += parseFloat(addon.per_unit_cost) || 0;
      }
      return total;
    }, 0);
  }

  /* ------------------------------------------
     LOGISTICS MULTIPLIER
  ------------------------------------------ */
  function getLogisticsMultiplier(logisticsId) {
    const options = logisticsOptions.logistics_options || logisticsOptions;
    const option = options.find(
      l => l.id === logisticsId
    );
    return option ? parseFloat(option.price_multiplier) : 1.0;
  }

  /* ------------------------------------------
     ECO INCENTIVE CALCULATOR
  ------------------------------------------ */
  function calcEcoIncentive(materialId, quantity) {
    let discount = 0;
    const rules = ecoRules.rules || [];
    rules.forEach((rule) => {
      if (
        rule.material_ids.includes(materialId) &&
        quantity >= rule.min_units
      ) {
        discount += rule.discount_amount;
      }
    });
    return discount;
  }

  /* ------------------------------------------
     UPDATE SIDEBAR DISPLAY
  ------------------------------------------ */
  function updateSidebar(unitPrice, quantity, ecoDiscount, total) {
    const unitPriceEl = $('efp-unit-price');
    const quantityEl = $('efp-quantity-display');
    const ecoLine = $('efp-eco-line');
    const ecoDiscountEl = $('efp-eco-discount');
    const totalEl = $('efp-total-price');

    if (unitPrice === null) {
      if (unitPriceEl) unitPriceEl.textContent = '—';
      if (quantityEl) quantityEl.textContent = '—';
      if (ecoLine) ecoLine.style.display = 'none';
      if (totalEl) totalEl.textContent = '$0.00';
      return;
    }

    if (unitPriceEl) unitPriceEl.textContent = `$${unitPrice.toFixed(2)}`;
    if (quantityEl) quantityEl.textContent = `${quantity.toLocaleString()} units`;

    if (ecoDiscount > 0) {
      if (ecoLine) ecoLine.style.display = 'flex';
      if (ecoDiscountEl) ecoDiscountEl.textContent = `-$${ecoDiscount.toFixed(2)}`;
    } else {
      if (ecoLine) ecoLine.style.display = 'none';
    }

    if (totalEl) totalEl.textContent = `$${total.toFixed(2)}`;
  }

  /* ------------------------------------------
     PREVIEW LABEL UPDATE
  ------------------------------------------ */
  function updatePreviewLabel() {
    const { pouch_type, material, size } = state;
    const previewEl = $('efp-preview-text');
    if (!previewEl) return;

    const pouchLabel = pouch_type
      ? pouch_type.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())
      : '—';
    const materialSpec = material && MATERIAL_SPECS[material]
      ? MATERIAL_SPECS[material].finish
      : '—';
    const sizeLabel = size ? size.toUpperCase() : '—';

    previewEl.textContent = `Visualizing: ${pouchLabel} / ${materialSpec} / ${sizeLabel}`;
  }

  /* ------------------------------------------
     QUANTITY CARD BINDING (STEP 6)
  ------------------------------------------ */
  function bindQuantityTiers() {
    $$('[data-type="quantity"]').forEach((btn) => {
      btn.addEventListener('click', () => {
        $$('[data-type="quantity"]').forEach(b => b.classList.remove('active'));
        btn.classList.add('active');
        state.quantity = parseInt(btn.dataset.value, 10);
        recalculate();
      });
    });
  }

  /* ------------------------------------------
     ADD TO CART
  ------------------------------------------ */
  function bindAddToCart() {
    const btn = $('efp-add-to-cart');
    if (!btn) return;

    btn.addEventListener('click', () => {
      if (btn.disabled) return;

      const { pouch_type, material, size, addons, quantity, logistics } = state;

      const unitPrice = getUnitPrice(pouch_type, material, size, String(quantity));
      const addonCostPerUnit = calcAddonCosts(addons);
      const logisticsMultiplier = getLogisticsMultiplier(logistics);
      const pricePerUnit = (unitPrice + addonCostPerUnit) * logisticsMultiplier;
      const ecoDiscount = calcEcoIncentive(material, quantity);
      const total = (pricePerUnit * quantity) - ecoDiscount;

      const properties = {
        'Pouch Type': pouch_type.replace(/_/g, ' '),
        'Material': material.replace(/_/g, ' '),
        'Size': size,
        'Quantity': quantity,
        'Add-Ons': addons.length ? addons.join(', ') : 'None',
        'Logistics': logistics,
        'Unit Price': `$${pricePerUnit.toFixed(2)}`,
        'Eco Incentive': ecoDiscount > 0 ? `-$${ecoDiscount.toFixed(2)}` : 'N/A',
        'Order Total': `$${total.toFixed(2)}`
      };

      addToCart(DATA.variantId, quantity, properties);
    });
  }

  /* ------------------------------------------
     SHOPIFY AJAX CART API
  ------------------------------------------ */
  function addToCart(variantId, quantity, properties) {
    const btn = $('efp-add-to-cart');
    btn.textContent = 'Adding...';
    btn.disabled = true;

    // Step 1: Add new EFP item directly (no replacement)
    fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        id: variantId,
        quantity: 1,
        properties: properties
      })
    })
    .then(res => res.json())
    .then(data => {
      if (data.status) {
        alert(`Error: ${data.description}`);
        btn.textContent = '🛒 Add to Cart';
        btn.disabled = false;
        return;
      }

      // Step 2: Calculate totals for ALL EFP items in cart
      return fetch('/cart.js')
        .then(res => res.json())
        .then(cart => {
          const efpItems = cart.items.filter(item =>
            item.properties && item.properties['Pouch Type']
          );

          // Sum up all EFP order totals
          let combinedEFPTotal = 0;
          efpItems.forEach(item => {
            const itemTotal = parseFloat(item.properties['Order Total']?.replace('$', '') || 0);
            combinedEFPTotal += itemTotal;
          });

          // Current item totals
          const { pouch_type, material, size, addons, quantity: qty, logistics } = state;
          const unitPrice = getUnitPrice(pouch_type, material, size, String(qty));
          const addonCostPerUnit = calcAddonCosts(addons);
          const logisticsMultiplier = getLogisticsMultiplier(logistics);
          const pricePerUnit = (unitPrice + addonCostPerUnit) * logisticsMultiplier;
          const ecoDiscount = calcEcoIncentive(material, qty);
          const total = (pricePerUnit * qty) - ecoDiscount;

          // Step 3: Update cart attributes to reflect latest addition
          return fetch('/cart/update.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              attributes: {
                'efp_order_total': total.toFixed(2),
                'efp_unit_price': pricePerUnit.toFixed(2),
                'efp_quantity': qty,
                'efp_pouch_type': pouch_type,
                'efp_material': material,
                'efp_size': size,
                'efp_logistics': logistics,
                'efp_addons': addons.join(','),
                'efp_eco_discount': ecoDiscount.toFixed(2)
              }
            })
          });
        });
    })
    .then(() => {
      window.location.href = '/cart';
    })
    .catch(err => {
      console.error('Cart error:', err);
      btn.textContent = '🛒 Add to Cart';
      btn.disabled = false;
    });
  }

  /* ------------------------------------------
     TOGGLE ADD TO CART BUTTON
  ------------------------------------------ */
  function toggleAddToCart(enabled) {
    const btn = $('efp-add-to-cart');
    if (!btn) return;
    btn.disabled = !enabled;
    btn.textContent = '🛒 Add to Cart';
  }

  /* ------------------------------------------
     NAV HIGHLIGHT ON SCROLL
  ------------------------------------------ */
  function highlightNavOnScroll() {
    const steps = $$('.efp-step');
    const navItems = $$('.efp-nav__item');

    if (!steps.length || !navItems.length) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const stepId = entry.target.id;
            const stepNum = stepId.replace('step-', '');
            navItems.forEach((item) => {
              item.classList.toggle(
                'active',
                item.dataset.step === stepNum
              );
            });
          }
        });
      },
      { threshold: 0.4 }
    );

    steps.forEach((step) => observer.observe(step));
  }

  /* ------------------------------------------
     START
  ------------------------------------------ */
  document.addEventListener('DOMContentLoaded', () => {
    if (!window.EFP_DATA) {
      console.warn('EFP Configurator: No metafield data found.');
      return;
    }
    init();
    bindQuantityTiers();
  });

  /* ------------------------------------------
     WATCH FOR EFP ITEM REMOVAL
  ------------------------------------------ */
  function watchCartForEFPRemoval() {
    // Listen for any remove link clicks on the cart page
    document.querySelectorAll('a[href*="quantity=0"], .remove').forEach((btn) => {
      btn.addEventListener('click', function () {
        const cartItem = this.closest('[data-js-cart-item]');
        if (!cartItem) return;

        // Check if this is the EFP item by looking for Pouch Type property
        const itemTitle = cartItem.querySelector('.cart-item__title');
        const properties = cartItem.querySelectorAll('.cart-item__property');
        let isEFPItem = false;

        properties.forEach((prop) => {
          if (prop.textContent.includes('Pouch Type')) {
            isEFPItem = true;
          }
        });

        if (isEFPItem) {
          clearEFPCartAttributes();
        }
      });
    });
  }

  /* ------------------------------------------
     CLEAR EFP CART ATTRIBUTES
  ------------------------------------------ */
  function clearEFPCartAttributes() {
    fetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        attributes: {
          'efp_order_total': '',
          'efp_unit_price': '',
          'efp_quantity': '',
          'efp_pouch_type': '',
          'efp_material': '',
          'efp_size': '',
          'efp_logistics': '',
          'efp_addons': '',
          'efp_eco_discount': ''
        }
      })
    })
    .then(() => {
      // Reload page to reflect updated totals
      window.location.reload();
    })
    .catch((err) => {
      console.error('Failed to clear EFP cart attributes:', err);
    });
  }

  /* ------------------------------------------
     LISTEN FOR AJAX CART UPDATES (theme events)
  ------------------------------------------ */
  document.addEventListener('cart-updated', function () {
    // Check if EFP item still exists in cart
    fetch('/cart.js')
      .then(res => res.json())
      .then(cart => {
        const hasEFPItem = cart.items.some(item =>
          item.properties && item.properties['Pouch Type']
        );
        if (!hasEFPItem && cart.attributes && cart.attributes.efp_order_total) {
          clearEFPCartAttributes();
        }
      });
  });

})();